import CodeIcon from "@mui/icons-material/Code";
import DownloadSharpIcon from "@mui/icons-material/DownloadSharp";
import FormatAlignCenterSharpIcon from "@mui/icons-material/FormatAlignCenterSharp";
import FormatAlignLeftSharpIcon from "@mui/icons-material/FormatAlignLeftSharp";
import FormatAlignRightSharpIcon from "@mui/icons-material/FormatAlignRightSharp";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatColorFillSharpIcon from "@mui/icons-material/FormatColorFillSharp";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatListBulletedSharpIcon from "@mui/icons-material/FormatListBulletedSharp";
import FormatListNumberedSharp from "@mui/icons-material/FormatListNumberedSharp";
import FormatQuoteSharpIcon from "@mui/icons-material/FormatQuoteSharp";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import ImageSharpIcon from "@mui/icons-material/ImageSharp";
import InsertLinkSharpIcon from "@mui/icons-material/InsertLinkSharp";
import LinkOffSharpIcon from "@mui/icons-material/LinkOffSharp";
import StrikethroughSIcon from "@mui/icons-material/StrikethroughS";
import TextFormatSharpIcon from "@mui/icons-material/TextFormatSharp";
import TextIncreaseSharpIcon from "@mui/icons-material/TextIncreaseSharp";
import { Box, Stack, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import { useContext, useState } from "react";
import { styled } from "@mui/material/styles";
import { SnackbarContext } from "components/context/SnackbarProvider";
import Button from "components/ui/atoms/Button";
import Dialog from "components/ui/atoms/Dialog";
import Highlight from "components/ui/atoms/Highlight";
import TextField from "components/ui/atoms/TextField";

const StyledIconButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== "$isActive",
})(({ theme, $isActive }) => ({
  padding: "5px",
  fontSize: theme.fontSize.md,
  color: $isActive ? theme.palette_custom.darkBlue : "#37352f80",
  borderRadius: "0px",
  "&:hover": {
    color: theme.palette_custom.darkBlue,
    backgroundColor: "inherit",
  },
}));

const StyledColorIconButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== "$isActive" && prop !== "$color",
})(({ theme, $isActive, $color }) => ({
  fontSize: theme.fontSize.md,
  borderRadius: "0px",
  color: $isActive ? $color : "#37352f80",
  "&:hover": {
    color: $color,
    backgroundColor: "inherit",
  },
}));

const StyledInputFile = styled("input")({
  width: "0.1px",
  height: "0.1px",
  opacity: 0,
  overflow: "hidden",
  position: "absolute",
  zIndex: -1,
});

const FixedMenu = ({ editor }) => {
  const [openLink, setOpenLink] = useState(false);
  const [openImageUploader, setOpenImageUploader] = useState(false);
  const [file, setFile] = useState({});
  const [link, setLink] = useState("");
  const theme = useTheme();
  const { displayMessage, displayError } = useContext(SnackbarContext);

  const copyToClipboard = (text) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(
        () => displayMessage("The html is in your clipboard."),
        () => displayError("[ERROR] Copying to the clipboard failed!"),
      );
    } else {
      displayError(
        "[ERROR] Copying to the clipboard failed! (no clipboard api in navigator)",
      );
    }
  };

  const handleLink = (event) => {
    var url = event.target.value;
    if (!url.startsWith("http://") && !url.startsWith("https://")) {
      url = "https://" + url;
    }
    setLink(url);
  };
  const handleFile = (event) => {
    let file = event.target.files[0];
    let reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = function () {
      setFile({ name: file.name, base64Url: reader.result });
    };
  };

  return (
    editor && (
      <>
        <Dialog
          title={
            <Highlight color={theme.palette_custom.yellow}>
              Add a link
            </Highlight>
          }
          content={
            <Box mt={2} mb={2}>
              <TextField
                title="Link"
                value={link}
                onBlur={(event) => handleLink(event)}
                handleOnChange={(event) => {
                  setLink(event.target.value);
                }}
              />
            </Box>
          }
          open={openLink}
          handleClose={() => {
            setOpenLink(false);
          }}
          actions={
            <>
              <Button
                variant="contained"
                onClick={() => {
                  editor
                    .chain()
                    .focus()
                    .extendMarkRange("link")
                    .setLink({ href: link })
                    .run();
                  setLink("");
                  setOpenLink(false);
                }}
              >
                Apply
              </Button>
              <Button variant="text" onClick={() => setOpenLink(false)}>
                Close
              </Button>
            </>
          }
        ></Dialog>
        <Dialog
          title={
            <Highlight color={theme.palette_custom.yellow}>Add image</Highlight>
          }
          content={
            <Grid container alignItems="center">
              <Grid size={{ md: 4 }}>
                <StyledInputFile
                  type="file"
                  name="file"
                  id="file"
                  onChange={handleFile}
                />
                <Button
                  variant="contained"
                  startIcon={<DownloadSharpIcon sx={{ scale: "0.75" }} />}
                >
                  <label htmlFor="file">Choose an image</label>
                </Button>
              </Grid>
              {file?.name && <Grid size={{ md: 8 }}>{file?.name}</Grid>}
            </Grid>
          }
          open={openImageUploader}
          handleClose={() => {
            setOpenImageUploader(false);
          }}
          actions={
            <>
              <Button
                variant="contained"
                onClick={() => {
                  editor
                    .chain()
                    .focus()
                    .setImage({
                      src: file?.base64Url,
                    })
                    .run();
                  setFile({});
                  setOpenImageUploader(false);
                }}
              >
                Import
              </Button>
              <Button
                variant="text"
                onClick={() => setOpenImageUploader(false)}
              >
                Close
              </Button>
            </>
          }
        ></Dialog>
        <Stack spacing={0} direction="row" flexWrap="wrap">
          <StyledIconButton
            disableRipple
            aria-label="bold"
            onClick={() => editor.chain().focus().toggleBold().run()}
            $isActive={editor.isActive("bold")}
          >
            <FormatBoldIcon sx={{ scale: "0.8" }} />
          </StyledIconButton>
          <StyledIconButton
            disableRipple
            aria-label="italic"
            onClick={() => editor.chain().focus().toggleItalic().run()}
            $isActive={editor.isActive("italic")}
          >
            <FormatItalicIcon sx={{ scale: "0.8" }} />
          </StyledIconButton>
          <StyledIconButton
            disableRipple
            aria-label="underline"
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            $isActive={editor.isActive("underline")}
          >
            <FormatUnderlinedIcon sx={{ scale: "0.8" }} />
          </StyledIconButton>
          <StyledIconButton
            disableRipple
            aria-label="strike"
            onClick={() => editor.chain().focus().toggleStrike().run()}
            $isActive={editor.isActive("strike")}
          >
            <StrikethroughSIcon sx={{ scale: "0.8" }} />
          </StyledIconButton>
          <StyledIconButton
            disableRipple
            $isActive={editor.isActive("link")}
            onClick={() =>
              editor.isActive("link")
                ? editor.chain().unsetLink().run()
                : setOpenLink(!openLink)
            }
          >
            {editor.isActive("link") ? (
              <LinkOffSharpIcon
                sx={{ transform: "rotate(60deg)", scale: "0.7" }}
              />
            ) : (
              <InsertLinkSharpIcon
                sx={{ transform: "rotate(60deg)", scale: "0.8" }}
              />
            )}
          </StyledIconButton>
          <StyledColorIconButton
            disableRipple
            onClick={() => {
              !editor.isActive("textStyle", {
                color: theme.palette_custom.oos,
              })
                ? editor
                    .chain()
                    .focus()
                    .setColor(theme.palette_custom.oos)
                    .run()
                : editor.chain().focus().unsetColor().run();
            }}
            $isActive={editor.isActive("textStyle", {
              color: theme.palette_custom.oos,
            })}
            $color={theme.palette_custom.oos}
          >
            <TextFormatSharpIcon />
          </StyledColorIconButton>
          <StyledColorIconButton
            disableRipple
            onClick={() => {
              !editor.isActive("highlight", {
                color: theme.palette_custom.yellow,
              })
                ? editor
                    .chain()
                    .focus()
                    .toggleHighlight({
                      color: theme.palette_custom.yellow,
                    })
                    .run()
                : editor.chain().focus().unsetHighlight().run();
            }}
            $isActive={editor.isActive("highlight", {
              color: theme.palette_custom.yellow,
            })}
            $color={theme.palette_custom.yellow}
          >
            <FormatColorFillSharpIcon sx={{ scale: "0.65" }} />
          </StyledColorIconButton>
          <StyledIconButton
            disableRipple
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 3 }).run()
            }
            $isActive={editor.isActive("heading", { level: 3 })}
          >
            <TextIncreaseSharpIcon sx={{ scale: "0.6" }} />
          </StyledIconButton>
          <StyledIconButton
            disableRipple
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 2 }).run()
            }
            $isActive={editor.isActive("heading", { level: 2 })}
          >
            <TextIncreaseSharpIcon sx={{ scale: "0.7" }} />
          </StyledIconButton>
          <StyledIconButton
            disableRipple
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 1 }).run()
            }
            $isActive={editor.isActive("heading", { level: 1 })}
          >
            <TextIncreaseSharpIcon sx={{ scale: "0.7" }} />
          </StyledIconButton>
          <StyledIconButton
            disableRipple
            aria-label="align-left"
            onClick={() => editor.chain().focus().setTextAlign("left").run()}
            $isActive={editor.isActive({ textAlign: "left" })}
          >
            <FormatAlignLeftSharpIcon sx={{ scale: "0.8" }} />
          </StyledIconButton>
          <StyledIconButton
            disableRipple
            aria-label="align-center"
            onClick={() => editor.chain().focus().setTextAlign("center").run()}
            $isActive={editor.isActive({ textAlign: "center" })}
          >
            <FormatAlignCenterSharpIcon sx={{ scale: "0.8" }} />
          </StyledIconButton>
          <StyledIconButton
            disableRipple
            aria-label="align-right"
            onClick={() => editor.chain().focus().setTextAlign("right").run()}
            $isActive={editor.isActive({ textAlign: "right" })}
          >
            <FormatAlignRightSharpIcon sx={{ scale: "0.8" }} />
          </StyledIconButton>
          <StyledIconButton
            disableRipple
            aria-label="bullet-list"
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            $isActive={editor.isActive("bulletList")}
          >
            <FormatListBulletedSharpIcon sx={{ scale: "0.8" }} />
          </StyledIconButton>
          <StyledIconButton
            disableRipple
            aria-label="bullet-list"
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            $isActive={editor.isActive("orderedList")}
          >
            <FormatListNumberedSharp sx={{ scale: "0.8" }} />
          </StyledIconButton>
          <StyledIconButton
            disableRipple
            onClick={() => {
              editor.chain().focus().toggleBlockquote().run();
            }}
            $isActive={editor.isActive("blockquote")}
          >
            <FormatQuoteSharpIcon sx={{ scale: "0.8" }} />
          </StyledIconButton>
          <StyledIconButton
            disableRipple
            onClick={() => setOpenImageUploader(!openImageUploader)}
            $isActive={openImageUploader}
          >
            <ImageSharpIcon sx={{ scale: "0.8" }} />
          </StyledIconButton>
          <StyledIconButton
            disableRipple
            onClick={() => copyToClipboard(editor.getHTML())}
            $isActive={openImageUploader}
          >
            <CodeIcon sx={{ scale: "0.8" }} />
          </StyledIconButton>
        </Stack>
      </>
    )
  );
};

export default FixedMenu;
